import * as React from 'react';
import { isEmpty, snakeCase } from 'lodash';
import { connect } from 'react-redux';
import classnames from 'classnames';

import AttachmentsWithViewDelete from 'components/attachments-with-view-delete/AttachmentsWithViewDelete';
import DeliverableTagView from './deliverable-tag-view/DeliverableTagView';
import DeliverableLinkView from './deliverable-link-view/DeliverableLinkView';
import DeliverableTimelineView from './deliverable-timeline-view/DeliverableTimelineView';
import DeliverableInfoCopyControl from './deliverable-info-copy-control/DeliverableInfoCopyControl';
import RichTextEditor from 'components/inputs/rich-text-editor/RichTextEditor';

import { __t, applyTranslations } from 'utils/language';
import {
  CONTENT_APPROVAL_TIMELINE,
  CONTENT_GO_LIVE_TIMELINE,
  DELIVERABLE_ACTION_HASHTAGS,
  DELIVERABLE_ACTION_MENTIONS,
  DELIVERABLE_ACTION_TAGS,
  DELIVERABLE_TYPE_OTHER,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT,
  INSIGHTS_PROVISION_TIMELINE,
  isEntertainmentVideo
} from 'utils/deliverables';

import { Deliverable } from 'types';

import './deliverable-information-tab.scss';

applyTranslations(require('./deliverable-information-tab.i18n.json'));

type OwnProps = {
  deliverable: Deliverable,
  isCcm?: boolean
}

type StateProps = {
  isMultiZLinkEnabled?: boolean,
  outfitGuidelineLink?: string
}

type Props = OwnProps & StateProps;

const ACTIONS = [DELIVERABLE_ACTION_HASHTAGS, DELIVERABLE_ACTION_TAGS, DELIVERABLE_ACTION_MENTIONS];

const DeliverableInformationTab = ({
  isMultiZLinkEnabled,
  outfitGuidelineLink = '#',
  deliverable,
  isCcm
}: Props) => {
  const { id, links, guidelinesAttachments, deliverableType } = deliverable;
  const isTaskTypeOther = deliverableType === DELIVERABLE_TYPE_OTHER;
  const isEntertainmentVideoTask = isEntertainmentVideo(deliverableType);
  // eslint-disable-next-line no-nested-ternary
  const timelineTypes = isTaskTypeOther
    ? [CONTENT_GO_LIVE_TIMELINE]
    : isEntertainmentVideoTask
      ? [CONTENT_APPROVAL_TIMELINE, CONTENT_GO_LIVE_TIMELINE]
      : [CONTENT_APPROVAL_TIMELINE, CONTENT_GO_LIVE_TIMELINE, INSIGHTS_PROVISION_TIMELINE];

  const nonEmptyTags = ACTIONS.filter(action => (deliverable[action]?.length
    && deliverable[action][0]));

  const hasTrackingLink = !isEmpty(deliverable.trackingLinks);

  const renderZalandoOutfitInfo = (): React.ReactNode => {
    const outfitInfoClasses = classnames('deliverable-information-tab__zalando-outfit-info', {
      'deliverable-information-tab__zalando-outfit-info--ccm': isCcm
    });

    return (
      <div className={outfitInfoClasses}>
        <div className="deliverable-information-tab__num-outfits">
          <div className="deliverable-information-tab__num-outfits-label">
            {__t('deliverable_information_tab.num_outfits')}:
          </div>
          <div className="deliverable-information-tab__num-outfits-count">
            {deliverable.numOutfits}
          </div>
        </div>
        {
          isCcm && (
            <div className="deliverable-information-tab__outfit-guidelines">
              <div className="deliverable-information-tab__outfit-guidelines-header">
                {__t('deliverable_information_tab.section_title.attachments')}
              </div>
              <a
                href={outfitGuidelineLink}
                rel="noopener noreferrer"
                target="_blank"
                className="deliverable-information-tab__outfit-guidelines-link"
              >
                {__t('deliverable_information_tab.outfit_guidelines')}
              </a>
            </div>
          )
        }
      </div>
    );
  };

  return (
    <div className="deliverable-information-tab">
      <div className="deliverable-information-tab__title">
        {__t('deliverable_information_tab.title')}
      </div>
      <div className="deliverable-information-tab__section">
        {
          !isTaskTypeOther && (
            <div className="deliverable-information-tab__section-title">
              {__t('deliverable_information_tab.section_title.timeline')}
            </div>
          )
        }
        <div className="deliverable-information-tab__timeline">
          {
            timelineTypes.map(timelineType => (
              <DeliverableTimelineView
                key={`${id}-${timelineType}`}
                startDate={deliverable[timelineType]?.startDate}
                endDate={deliverable[timelineType]?.endDate}
                title={__t(`deliverable_information_tab.timeline_title.${isTaskTypeOther ? 'timeframe_timeline' : snakeCase(timelineType)}`)}
              />
            ))
          }
        </div>
      </div>
      {
        deliverableType === DELIVERABLE_TYPE_ZALANDO_OUTFIT &&
          renderZalandoOutfitInfo()
      }
      <div className="deliverable-information-tab__description-attachment-container">
        {
          deliverable.description && (
            <div className="deliverable-information-tab__section deliverable-information-tab__description">
              <div className="deliverable-information-tab__section-title">
                {__t('deliverable_information_tab.section_title.task_description')}
              </div>
              <div className="deliverable-information-tab__description-text">
                <RichTextEditor
                  value={deliverable.description}
                />
              </div>
            </div>
          )
        }
        {
          !!guidelinesAttachments?.length && (
            <div className="deliverable-information-tab__section deliverable-information-tab__attachments">
              <AttachmentsWithViewDelete
                attachments={guidelinesAttachments}
                isViewMode
                uniqueId={id}
              />
            </div>
          )
        }
      </div>
      {
        !!nonEmptyTags.length && (
          <div className="deliverable-information-tab__section deliverable-information-tab__tags">
            {
              nonEmptyTags
                .map((action) => (
                  <DeliverableTagView
                    key={`${id}-${action}`}
                    deliverableActionType={action}
                    tags={deliverable[action]}
                    title={__t(`deliverable_information_tab.section_title.${action}`)}
                    deliverableId={id}
                  />
                ))
            }
          </div>
        )
      }
      {
        !!deliverable.location && (
          <div className="deliverable-information-tab__section">
            <div className="deliverable-information-tab__section-title deliverable-information-tab__location">
              {__t('deliverable_information_tab.section_title.location')}
              <DeliverableInfoCopyControl
                uniqueKey={`${deliverable.id}-location`}
                wrapperClass="deliverable-information-tab__location-copy"
              />
            </div>
            <div
              className="deliverable-information-tab__description-text"
              data-copy-key={`${deliverable.id}-location-field-to-copy`}
            >
              {deliverable.location}
            </div>
          </div>
        )
      }
      {
        hasTrackingLink && (
          <div className="deliverable-information-tab__section deliverable-information-tab__links">
            {
              deliverable.trackingLinks.map((trackingLink, index) => (
                <DeliverableLinkView
                  key={trackingLink.shortUrl}
                  deliverableId={id}
                  deliverableLink={{
                    name: trackingLink.name,
                    url: trackingLink.shortUrl || '',
                    description: trackingLink.description || ''
                  }}
                  title={__t('deliverable_information_tab.section_title.landing_page')}
                  descriptionTitle={__t('deliverable_information_tab.section_title.link_description')}
                  nameTitle={__t('deliverable_information_tab.section_title.name_title')}
                  index={index}
                  isMultiZLinkEnabled={isMultiZLinkEnabled}
                />
              ))
            }
          </div>
        )
      }
      {
        !!links?.length && (
          <div className="deliverable-information-tab__section deliverable-information-tab__links">
            {
              links?.map((link, index) => (
                <DeliverableLinkView
                  key={`${id}-${link.url}`}
                  deliverableId={id}
                  deliverableLink={link}
                  title={
                    hasTrackingLink
                      ? __t('deliverable_information_tab.section_title.other_link')
                      : __t('deliverable_information_tab.section_title.link')
                  }
                  descriptionTitle={__t('deliverable_information_tab.section_title.link_description')}
                  index={index}
                />
              ))
            }
          </div>
        )
      }
    </div>
  );
};

const mapStateToProps = ({ runtimeConfig }): StateProps => ({
  isMultiZLinkEnabled: runtimeConfig.config.isMultiZLinkEnabled,
  outfitGuidelineLink: runtimeConfig.config.outfitGuidelineLink
});

export default connect(mapStateToProps, null)(DeliverableInformationTab);
